import { useEffect, useState } from "react";
import { adminClient, genericOAuthClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import type { User } from "./types";

export const authClient = createAuthClient({
  baseURL: typeof window !== "undefined" ? window.location.origin : "http://localhost:3007",
  plugins: [adminClient(), genericOAuthClient()],
});

export const { signIn, signUp, signOut, useSession } = authClient;

export interface AuthMethods {
  entra: boolean;
  localAuth: boolean;
}

/** Fetches which sign-in methods the server has enabled (see /api/config). */
export function useAuthMethods(): { methods: AuthMethods | null } {
  const [methods, setMethods] = useState<AuthMethods | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/config", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { entra: true, localAuth: false }))
      .then((m: AuthMethods) => {
        if (alive) setMethods(m);
      })
      .catch(() => {
        // Fall back to SSO-only if config can't be reached.
        if (alive) setMethods({ entra: true, localAuth: false });
      });
    return () => {
      alive = false;
    };
  }, []);
  return { methods };
}

/** Local email+password sign-in. Returns an error message, or null on success. */
export async function signInWithPassword(
  email: string,
  password: string,
): Promise<string | null> {
  const res = await signIn.email({ email, password });
  return res.error ? res.error.message ?? "Sign-in failed." : null;
}

/** Local email+password sign-up. Returns an error message, or null on success. */
export async function signUpWithPassword(
  name: string,
  email: string,
  password: string,
): Promise<string | null> {
  const res = await signUp.email({ name, email, password });
  return res.error ? res.error.message ?? "Sign-up failed." : null;
}

export const DEFAULT_USER: User = {
  id: "u_erin",
  name: "Erin Walsh",
  email: "erin.walsh@nymbl.app",
  initials: "EW",
  role: "Product Manager",
};

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

// Bridges better-auth's session into the {user, pending, signIn, signOut}
// shape the existing screens already consume.
export function useAuth() {
  const { data, isPending } = useSession();

  const user: User | null = data?.user
    ? {
        id: data.user.id,
        name: data.user.name,
        email: data.user.email,
        initials: initialsOf(data.user.name || data.user.email),
        role: "Product Manager",
        isAdmin: (data.user as { role?: string }).role === "admin",
      }
    : null;

  async function signInWithMicrosoft() {
    const res = await signIn.oauth2({
      providerId: "microsoft-entra-id",
      callbackURL: `${window.location.origin}/dashboard`,
    });
    // Newer better-auth versions auto-navigate; older ones return the URL and
    // expect the caller to redirect. Cover both paths.
    const url = (res as any)?.data?.url ?? (res as any)?.url;
    if (url && typeof window !== "undefined") {
      window.location.href = url;
    }
  }

  function signOutCurrent() {
    void signOut();
  }

  return {
    user,
    pending: isPending,
    signIn: signInWithMicrosoft,
    signOut: signOutCurrent,
    DEFAULT_USER,
  };
}
